import type { DomainError } from '@core/domain';

/**
 * Dialog step enumeration
 */
export enum DialogStep {
  POLICY_INFO = 'policy-info',
  TEXT_LABELS = 'text-labels',
  IMAGE_LABELS = 'image-labels',
  USERNAME_LABELS = 'username-labels',
  CUSTOM_LABELS = 'custom-labels',
  REVIEW = 'review',
}

/**
 * Policy information step data (Step 1)
 */
export interface DialogPolicyInfo {
  name: string;
  description: string;
  isEnabled: boolean;
}

/**
 * Generic dialog label — severity and scoreThreshold are optional.
 * Present if the label has a value, omitted for presence-based labels.
 */
export interface DialogLabel {
  labelId: string;
  severity?: number;
  scoreThreshold?: number;
}

/**
 * Text labels step data (Step 2)
 */
export interface DialogTextLabels {
  textLabels: DialogLabel[];
}

/**
 * Image labels step data (Step 3)
 */
export interface DialogImageLabels {
  imageLabels: DialogLabel[];
}

/**
 * Username labels step data (Step 4)
 */
export interface DialogUsernameLabels {
  usernameLabels: DialogLabel[];
}

/**
 * Presence-based label (Custom labels have no severity/score, just presence)
 */
export interface PresenceLabel {
  labelId: string;
  labelName?: string;
}

/**
 * Custom labels step data (Moderation workflow only)
 */
export interface DialogCustomLabels {
  customLabels: PresenceLabel[];
}

/**
 * Review step data (Last step)
 */
export interface DialogReview {
  isValid: boolean;
  warnings: string[];
}

/**
 * Complete dialog form data
 */
export interface DialogFormData {
  policyInfo: DialogPolicyInfo;
  textLabels: DialogTextLabels;
  imageLabels: DialogImageLabels;
  usernameLabels: DialogUsernameLabels;
  customLabels: DialogCustomLabels;
  review: DialogReview;
}

/**
 * Dialog state interface
 */
export interface DialogState {
  currentStep: DialogStep;
  formData: DialogFormData;
  isValid: boolean;
  isSubmitting: boolean;
  errors: DomainError[];
}
